import React, { useCallback, useEffect, useRef } from "react";
import { View, Flex, Button } from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/storeConfig";
import LiveCall from "./LiveCall";
import ConferenceLiveCall from "./ConferenceLiveCall";
import Emitter from "../../../emitter";
import { API_CALL_MERGE } from "../../../constants/constants";
import { setConference } from "../../../actions/call";
import storeRTK from "../../../store/storeRTK";
import { mergeIcon } from "../../common/Icons";
import { addAttributesToCall } from "../../../api/session-api";

interface ConferenceCallProps {
  primaryCall?: any;
  secondaryCall?: any;
  onEndConference?: () => void;
  onRemoveCall?: (callId: string) => void;
}

/**
 * MultiCallContainer manages the display of multiple calls and merge functionality
 *
 * Key Features:
 * - Displays primary and secondary (conference) calls
 * - Shows merge button when conditions are met
 * - Tracks merge completion: calls addAttributesToCall with {merge_completed: true}
 *   when merge button becomes hidden for the first time
 * - Does NOT track barge-in completion (only merge operations)
 */

// Reusable button styles
const ACTION_BUTTON_STYLES = {
  width: "94px",
  height: "32px",
  background: "#FFFFFF00",
  border: "2px solid #0265DC",
  borderRadius: "16px",
  opacity: 1,
  fontSize: "14px",
  fontWeight: "500",
  color: "#0265DC",
  cursor: "pointer",
  padding: "0",
  minHeight: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
} as const;

// Reusable Action Button Component
interface ActionButtonProps {
  onClick: () => void;
  className: string;
  icon: () => React.ReactElement;
  label: string;
}

const ActionButton = ({
  onClick,
  className,
  icon,
  label,
}: ActionButtonProps) => (
  <Button
    variant="secondary"
    onPress={onClick}
    UNSAFE_className={className}
    UNSAFE_style={ACTION_BUTTON_STYLES}
  >
    {icon()}
    {label}
  </Button>
);

export const MultiCallContainer = ({ primaryCall }: ConferenceCallProps) => {
  // Optimized selectors - destructure what we need
  const { call: globalCall, conference } = useSelector((state: RootState) => ({
    call: state.call,
    conference: (state as any).conference,
  }));
  const call1 = primaryCall || globalCall;

  // Refs to track merge button visibility state
  const previousShouldShowMergeRef = useRef<boolean | null>(null);
  const hasCalledMergeHiddenAPIRef = useRef<boolean>(false);

  // Visibility flags
  const shouldShowPrimary = Boolean(call1?.id);
  const shouldShowSecondary = Boolean(
    conference?.id && !call1?.isSilentMonitor
  );

  // Common state calculations
  const hasActiveCalls = Boolean(call1?.id && conference?.id);
  const isNotBarge = !call1?.isBarge;
  const isNotUnderSupervision = !conference?.isUnderSupervision;
  const hasValidCall = Boolean(call1?.id && call1?.self);

  // Button visibility
  const baseConditions =
    hasActiveCalls && isNotBarge && isNotUnderSupervision && hasValidCall;

  // Check if both calls are not in "DIALING" state
  const areBothCallsNotDialing =
    call1?.self?.state !== "DIALING" && conference?.self?.state !== "DIALING";

  // Check if either call is on hold
  const isEitherCallOnHold = globalCall?.self?.held || conference?.self?.held;

  const shouldShowMerge =
    baseConditions &&
    !call1?.beingBarged &&
    areBothCallsNotDialing &&
    isEitherCallOnHold;
  // const shouldShowSwap =
  //   baseConditions && !areBothCallsMuted && !areBothCallsOnHold;

  // Track when shouldShowMerge changes from true to false (first time only)
  // This happens when:
  // - Merge is completed successfully
  // - Call state changes making merge unavailable
  // - One of the calls ends
  useEffect(() => {
    const previousShouldShowMerge = previousShouldShowMergeRef.current;

    // Check if shouldShowMerge changed from true to false for the first time
    if (
      previousShouldShowMerge === true &&
      shouldShowMerge === false &&
      !hasCalledMergeHiddenAPIRef.current &&
      call1?.id // Ensure we have a valid call
    ) {
      // Mark that we've called the API to prevent duplicate calls
      hasCalledMergeHiddenAPIRef.current = true;

      // Add attribute to track that merge button became hidden/unavailable
      addAttributesToCall(call1.id, { AIC_IS_CONFERENCE: true })
        .then(() => {
          console.log(
            "MultiCallContainer: Successfully added merge_completed attribute to call"
          );
        })
        .catch((error) => {
          console.error(
            "MultiCallContainer: Failed to add merge_completed attribute to call:",
            error
          );
        });
    }

    // Update the previous state
    previousShouldShowMergeRef.current = shouldShowMerge;
  }, [shouldShowMerge, call1?.id]);

  // Reset tracking when call changes (new call scenario)
  useEffect(() => {
    // Reset the tracking flags when we get a new call
    hasCalledMergeHiddenAPIRef.current = false;
    previousShouldShowMergeRef.current = null;
  }, [call1?.id]);

  // Event handlers
  const handleMergeCall = useCallback(() => {
    Emitter.emit(API_CALL_MERGE, {});
    storeRTK.dispatch(
      setConference({
        ...conference,
        isMerged: !conference?.isMerged,
      })
    );
  }, [conference]);

  // const handleSwapCall = useCallback(() => {
  //   // TODO: Implement swap functionality
  //   console.log("Swap button clicked");
  // }, []);

  return (
    <View paddingX={32}>
      <Flex direction="column">
        {/* Primary Call */}
        {shouldShowPrimary && (
          <LiveCall
            callData={call1}
            callType="primary"
            showMerge={true}
            isConferenceCall={true}
          />
        )}

        {/* Secondary Call */}
        {shouldShowSecondary && <ConferenceLiveCall />}

        {/* Action Buttons */}
        {shouldShowMerge && (
          <View paddingY="0.75rem">
            <Flex justifyContent="center" width="100%" gap="size-100">
              {/* {shouldShowSwap && (
                <ActionButton
                  onClick={handleSwapCall}
                  className="swap-calls-button"
                  icon={swapIcon}
                  label="Swap"
                />
              )} */}

              {shouldShowMerge && (
                <ActionButton
                  onClick={handleMergeCall}
                  className="merge-calls-button"
                  icon={mergeIcon}
                  label="Merge"
                />
              )}
            </Flex>
          </View>
        )}
      </Flex>
    </View>
  );
};

export default MultiCallContainer;
